import type { ConsentProvider } from '../types.js';

/**
 * Sourcepoint CMP consent provider.
 *
 * Reads consent from localStorage key `_sp_user_consent_{propertyId}`.
 * Property IDs: **10411** (production), **10412** (dev).
 *
 * Consent is derived from `consentStatus.rejectedAny` / `rejectedLI`:
 * - `rejectedAny === false` → full consent (tracking allowed)
 * - `rejectedAny === true && rejectedLI === false` → limited consent (tracking allowed)
 * - otherwise → no consent
 */

const SP_KEY_PREFIX = '_sp_user_consent_';

function findLatestSpKey(): string | null {
  let latest: string | null = null;
  let latestId = -1;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(SP_KEY_PREFIX)) {
      continue;
    }
    const id = Number(key.slice(SP_KEY_PREFIX.length));
    if (id > latestId) {
      latestId = id;
      latest = key;
    }
  }
  return latest;
}

export type SourcepointConsentStatus = {
  rejectedAny: boolean;
  rejectedLI?: boolean;
  hasConsentData?: boolean;
  consentedAll?: boolean;
};

export type SourcepointState = {
  storageKey: string;
  status: SourcepointConsentStatus;
  euconsent: string | null;
  raw: unknown;
};

/**
 * Reads and parses the latest Sourcepoint consent record from localStorage.
 * Returns `null` if no key is present or the payload is malformed.
 * Exposed for devtools/debug surfaces — most code should use `ConsentProvider.getConsent()` instead.
 */
export function readSourcepointState(): SourcepointState | null {
  const key = findLatestSpKey();
  if (!key) {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const status = parsed?.consentStatus;
    if (!status || typeof status.rejectedAny !== 'boolean') {
      return null;
    }

    return {
      storageKey: key,
      status: {
        rejectedAny: status.rejectedAny,
        rejectedLI: status.rejectedLI,
        hasConsentData: status.hasConsentData,
        consentedAll: status.consentedAll,
      },
      euconsent: typeof parsed?.euconsent === 'string' ? parsed.euconsent : null,
      raw: parsed,
    };
  } catch {
    return null;
  }
}

function readConsentStatus(): SourcepointConsentStatus | null {
  return readSourcepointState()?.status ?? null;
}

function readConsent(): boolean | null {
  const status = readConsentStatus();
  if (!status) {
    return null;
  }

  if (!status.hasConsentData) {
    return null;
  }

  // full consent or limited consent → tracking allowed
  if (!status.rejectedAny) {
    return true;
  }
  if (status.rejectedLI === false) {
    return true;
  }

  return false;
}

const SUBSCRIBE_POLL_INTERVAL = 2_000;

type SourcepointConfig = { privacyManagerId?: string | number };

let configuredPrivacyManagerId: string | number | null = null;

export function createSourcepointProvider(config?: SourcepointConfig): ConsentProvider {
  configuredPrivacyManagerId = config?.privacyManagerId ?? null;
  return { getConsent: readConsent, subscribe: subscribeToConsentChanges };
}

function subscribeToConsentChanges(listener: () => void): () => void {
  let lastSignature = consentSignature();
  const check = () => {
    const current = consentSignature();
    if (current === lastSignature) {
      return;
    }
    lastSignature = current;
    listener();
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && !event.key.startsWith(SP_KEY_PREFIX)) {
      return;
    }
    check();
  };
  window.addEventListener('storage', onStorage);

  let tcfListenerId: number | null = null;
  let tcfRegistered = false;
  // Sourcepoint's SDK loads async, so `__tcfapi` is typically undefined at subscribe time.
  // Retry on every poll tick until it appears, then stop attempting.
  const tryRegisterTcf = () => {
    if (tcfRegistered) {
      return;
    }
    const tcfapi = window.__tcfapi;
    if (typeof tcfapi !== 'function') {
      return;
    }
    tcfRegistered = true;
    tcfapi('addEventListener', 2, (tcData, success) => {
      if (!success) {
        return;
      }
      if (tcfListenerId === null && typeof tcData?.listenerId === 'number') {
        tcfListenerId = tcData.listenerId;
      }
      check();
    });
  };

  tryRegisterTcf();
  const pollId = window.setInterval(() => {
    tryRegisterTcf();
    check();
  }, SUBSCRIBE_POLL_INTERVAL);

  return () => {
    window.removeEventListener('storage', onStorage);
    window.clearInterval(pollId);
    if (tcfListenerId !== null && typeof window.__tcfapi === 'function') {
      // biome-ignore lint/suspicious/noEmptyBlockStatements: TCF API positional arg: the callback receives the removal-success result, which we ignore
      window.__tcfapi('removeEventListener', 2, () => {}, tcfListenerId);
    }
  };
}

function consentSignature(): string {
  const status = readConsentStatus();
  if (!status) {
    return 'none';
  }
  return `${status.hasConsentData ?? false}:${status.rejectedAny}:${status.rejectedLI}:${status.consentedAll ?? false}`;
}

/**
 * Opens the Sourcepoint GDPR privacy manager modal. No-op if the SDK is not loaded
 * or no `privacyManagerId` was passed to `createSourcepointProvider()`.
 * Consent changes propagate through `ConsentProvider.subscribe` — read via `useTrackingConsent()`.
 */
export function showPrivacyManager(): void {
  if (configuredPrivacyManagerId === null) {
    return;
  }
  window._sp_?.loadPrivacyManagerModal?.(configuredPrivacyManagerId);
}

type TcfData = { listenerId?: number };
type TcfApi = (
  command: 'addEventListener' | 'removeEventListener',
  version: 2,
  callback: (tcData: TcfData | null, success: boolean) => void,
  listenerId?: number,
) => void;

declare global {
  interface Window {
    _sp_?: {
      loadPrivacyManagerModal?: (privacyManagerId: string | number) => void;
    };
    __tcfapi?: TcfApi;
  }
}
